/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.1
 */
import { EPageAccountInfo } from '../../pages/PageAccountInfo/types/pageAccountInfoTypes';

export interface IRouteParams {
    campaignId?: string;
    clientId: string;
    inquiryId: string;
    orderId: string;
    subscriberId: string;
    newsId: string;
}

export const CLIENT_ID = ':clientId(\\d+)';
export const SUBSCRIBER_ID = ':subscriberId(\\d+)';
export const ROUTE_CAMPAIGN_ID = ':campaignId(\\d+)';
export const ROUTE_INQUIRY_ID = ':inquiryId(\\d+)';
export const ROUTE_ORDER_ID = ':orderId(\\w+)';
export const ROUTE_NEWS_ID = ':newsId(\\d+)';

export const RoutePath: IRoutePath = {
    account: '/accounts',
    accountInfo: `/account/info/${CLIENT_ID}`,
    accountInfoAccs: `/account/info/${CLIENT_ID}/${EPageAccountInfo.SHIPPING_ACCS}`,
    accountInfoExternal: `/account/accountInfo/${CLIENT_ID}`,
    accountInfoFinancial: `/account/info/${CLIENT_ID}/${EPageAccountInfo.FINANCIAL}`,
    accountInfoFinancialDiscounts: `/account/info/${CLIENT_ID}/${EPageAccountInfo.FINANCIAL_DISCOUNTS}`,
    accountInfoFinancialGeneral: `/account/info/${CLIENT_ID}/${EPageAccountInfo.FINANCIAL_GENERAL}`,
    accountInfoFinancialMultiBalances: `/account/info/${CLIENT_ID}/${EPageAccountInfo.FINANCIAL_MULTI_BALANCES}`,
    accountInfoGeneral: `/account/info/${CLIENT_ID}/${EPageAccountInfo.GENERAL}`,
    autoReportId: `/autoreport/${CLIENT_ID}`,
    autoReportIdAccounts: `/autoreport/${CLIENT_ID}/accounts`,
    autoReportIdAccountsEdit: `/autoreport/${CLIENT_ID}/accounts/edit`,
    autoReportIdFormatDelivery: `/autoreport/${CLIENT_ID}/format-delivery`,
    autoReportIdScheduleParameters: `/autoreport/${CLIENT_ID}/schedule-parameters`,
    autoReportIdSubscribers: `/autoreport/${CLIENT_ID}/subscribers`,
    autoReportIdSubscribersEdit: `/autoreport/${CLIENT_ID}/subscribers/edit`,
    bills: '/bills',
    billsAccount: `/bills/account/${CLIENT_ID}`,
    billsUnpaid: '/bills/unpaid',
    billsUnpaidAccount: `/bills/unpaid/account/${CLIENT_ID}`,
    callsDetailDailyExpensesAccount: '/wizard/calls-detail/account',
    callsDetailDailyExpensesAccountId: `/wizard/calls-detail/account/${CLIENT_ID}`,
    callsDetailDailyExpensesAccountSingle: `/wizard/calls-detail/account-single/${CLIENT_ID}`,
    callsDetailDailyExpensesAccountSingleSubscribers: `/wizard/calls-detail/account-single-subscribers/${CLIENT_ID}`,
    callsDetailDailyExpensesAccountSubscribers: '/wizard/calls-detail/account-subscribers',
    callsDetailDailyExpensesBillsAccount: '/wizard/calls-detail/bills-account',
    callsDetailDailyExpensesBillsAccountId: `/wizard/calls-detail/bills-account/${CLIENT_ID}`,
    callsDetailDailyExpensesBillsAccountSingle: `/wizard/calls-detail/bill-account-single/${CLIENT_ID}`,
    callsDetailDailyExpensesBillsAccountSingleSubscribers: `/wizard/calls-detail/bills-account-single-subscribers/${CLIENT_ID}`,
    callsDetailDailyExpensesBillsAccountsSubscribers: '/wizard/calls-detail/bills-accounts-subscribers',
    callsDetailDailyExpensesBillsUnpaidAccount: '/wizard/calls-detail/bills-unpaid-account',
    callsDetailDailyExpensesBillsUnpaidAccountId: `/wizard/calls-detail/bills-unpaid-account/${CLIENT_ID}`,
    callsDetailDailyExpensesBillsUnpaidAccountSingle: `/wizard/calls-detail/bill-unpaid-account-single/${CLIENT_ID}`,
    callsDetailDailyExpensesBillsUnpaidAccountSingleSubscribers: `/wizard/calls-detail/bills-unpaid-account-single-subscribers/${CLIENT_ID}`,
    callsDetailDailyExpensesBillsUnpaidAccountsSubscribers: '/wizard/calls-detail/bills-unpaid-accounts-subscribers',
    callsDetailDailyExpensesSubscriberMobile: '/wizard/calls-detail/subscriber-mobile',
    callsDetailExpensesAccountId: `/wizard/calls-detail/expenses/account/${CLIENT_ID}`,
    callsDetailExpensesAccountIdSubscribers: `/wizard/calls-detail/expenses/account/${CLIENT_ID}/subscribers`,
    callsDetailExpensesAccounts: '/wizard/calls-detail/expenses/accounts',
    callsDetailExpensesAccountsSubscribers: '/wizard/calls-detail/expenses/accounts/subscribers',
    callsDetailExpensesBillsAccountId: `/wizard/calls-detail/expenses/bills/account/${CLIENT_ID}`,
    callsDetailExpensesBillsAccountIdSubscribers: `/wizard/calls-detail/expenses/bills/account/${CLIENT_ID}/subscribers`,
    callsDetailExpensesBillsAccounts: '/wizard/calls-detail/expenses/bills/accounts',
    callsDetailExpensesBillsAccountsSubscribers: '/wizard/calls-detail/expenses/bills/accounts/subscribers',
    callsDetailExpensesBillsUnpaidAccountId: `/wizard/calls-detail/expenses/bills/account/${CLIENT_ID}`,
    callsDetailExpensesBillsUnpaidAccountIdSubscribers: `/wizard/calls-detail/expenses/bills/unpaid/account/${CLIENT_ID}/subscribers`,
    callsDetailExpensesBillsUnpaidAccounts: '/wizard/calls-detail/expenses/bills/unpaid/accounts',
    callsDetailExpensesBillsUnpaidAccountsSubscribers:
        '/wizard/calls-detail/expenses/bills/unpaid/accounts/subscribers',
    callsDetailExpensesSubscriberFixed: '/wizard/calls-detail/expenses/subscriber/fixed',
    callsDetailExpensesSubscriberMobile: '/wizard/calls-detail/expenses/subscriber/mobile',
    callsDetailFixed: '/wizard/calls-detail/fixed',
    callsDetailFixedInfo: `/wizard/calls-detail/fixed/${SUBSCRIBER_ID}`,
    callsDetailMobile: '/wizard/calls-detail/mobile',
    callsDetailMobileInfo: `/wizard/calls-detail/mobile/${SUBSCRIBER_ID}`,
    campaignInfo: `/campaigns/${ROUTE_CAMPAIGN_ID}`,
    campaigns: '/campaigns',
    campaignsAccount: '/campaigns/account',
    campaignsByClient: `/campaigns/byClient/${CLIENT_ID}`,
    campaignsByMsisdn: `/campaigns/byMsisdn/${SUBSCRIBER_ID}`,
    campaignsSubscriber: '/campaigns/subscriber',
    cashback: '/cashback',
    cashbackAccountId: `/cashback/${CLIENT_ID}`,
    cashbackControlAccountId: `/cashback/${CLIENT_ID}/control`,
    cashbackHistoryAccountId: `/cashback/${CLIENT_ID}/history`,
    changePassword: '/login/changePassword',
    collectiveProduct: '/collective-product',
    dashboard: '/dashboard',
    expensesAnalyzer: '/expenses',
    expensesAnalyzerAccount: '/expenses/account',
    expensesAnalyzerAccountId: `/expenses/account/${CLIENT_ID}`,
    expensesAnalyzerAccountIdSubscribers: `/expenses/account/${CLIENT_ID}/subscribers`,
    expensesAnalyzerAccountSubscribers: '/expenses/account/subscribers',
    expensesAnalyzerBillsAccountId: `/expenses/bills/account/${CLIENT_ID}`,
    expensesAnalyzerBillsAccountIdSubscribers: `/expenses/bills/account/${CLIENT_ID}/subscribers`,
    expensesAnalyzerBillsAccounts: '/expenses/bills/accounts',
    expensesAnalyzerBillsAccountsSubscribers: '/expenses/bills/accounts/subscribers',
    expensesAnalyzerBillsUnpaidAccountId: `/expenses/bills/unpaid/account/${CLIENT_ID}`,
    expensesAnalyzerBillsUnpaidAccountIdSubscribers: `/expenses/bills/unpaid/account/${CLIENT_ID}/subscribers`,
    expensesAnalyzerBillsUnpaidAccounts: '/expenses/bills/unpaid/accounts',
    expensesAnalyzerBillsUnpaidAccountsSubscribers: '/expenses/bills/unpaid/accounts/subscribers',
    expensesAnalyzerSubscriberFixed: '/expenses/subscriber/fixed',
    expensesAnalyzerSubscriberFixedId: `/expenses/subscriber/fixed/${CLIENT_ID}`,
    expensesAnalyzerSubscriberMobile: '/expenses/subscriber/mobile',
    expensesAnalyzerSubscriberMobileId: `/expenses/subscriber/mobile/${CLIENT_ID}`,
    fastInquiryCreate: '/fast-inquiry-create',
    fastAdvanceInvoice: '/fast-advance-invoice',
    fastPromisedPayment: '/fast-promised-payment',
    wizardFastBillsDoc: '/fast-bills-doc',
    wizardFastReportDoc: '/fast-report-doc',
    forbidden: '/forbidden',
    inquiries: '/inquiries',
    inquiryInfo: `/inquiries/inquiry/${ROUTE_INQUIRY_ID}`,
    labelsImport: '/wizard/labels-import',
    login: '/login',
    logout: '/logout',
    main: '/',
    moneyTransfer: '/wizard/money-transfer',
    moneyTransferAccountId: `/wizard/money-transfer/account/${CLIENT_ID}`,
    news: '/news',
    newsId: `/news/${ROUTE_NEWS_ID}`,
    newsCreate: '/news/create',
    newsPreview: `/news/preview/${ROUTE_NEWS_ID}`,
    offers: '/campaigns',
    orderId: `/order/${ROUTE_ORDER_ID}`,
    orders: '/orders',
    paymentsAccount: '/payments/account',
    paymentsAccountId: `/payments/account/${CLIENT_ID}`,
    products: '/products',
    productsFromSubscriberId: `/products/subscribers/${CLIENT_ID}`,
    productsFromSubscribers: '/products/subscribers',
    recovery: '/recovery',
    reports: '/reports',
    reportsConstructor: '/constructor',
    reportsConstructorFromAccount: `/constructor/account/info/${CLIENT_ID}`,
    reportsConstructorFromAccountList: '/constructor/accounts',
    reportsConstructorFromMobileList: '/subscribers/mobile/constructor',
    reportsConstructorFromAutoReports: '/constructor/settings/autoreports',
    reportsConstructorSubscriber: '/constructor/subscribers',
    serviceAccessChangeUser: '/service-access/change-user',
    serviceAccessLogin: '/service-access/login',
    settings: '/settings/:section?',
    settingsAutoReports: '/settings/autoreports',
    settingsLabels: '/settings/labels',
    settingsPositions: '/settings/positions',
    settingsProfile: '/settings/profile',
    settingsStatistics: '/settings/statistics',
    settingsNotifications: '/settings/notifications',
    subscriberFixed: '/subscribers/fixed',
    subscriberFixedInfo: '/fixedSubscriber/info',
    subscriberId: `/subscriber/${CLIENT_ID}`,
    subscriberIdAdditionalAccounts: `/subscriber/${CLIENT_ID}/additional-accounts`,
    subscriberIdDcRule: `/subscriber/${CLIENT_ID}/dcrule`,
    subscriberIdFinances: `/subscriber/${CLIENT_ID}/finances`,
    subscriberIdInfo: `/subscriber/${CLIENT_ID}/info`,
    subscriberIdManageNumber: `/subscriber/${CLIENT_ID}/manage-number`,
    subscriberIdPersonalData: `/subscriber/${CLIENT_ID}/personal-data`,
    subscriberIdProducts: `/subscriber/${CLIENT_ID}/products`,
    subscriberMobile: '/subscribers/mobile',
    unauthorized: '/unauthorized',

    // wizards
    wizardAdditionalAccountsFromSubscriberId: `/wizard/additional-accounts/subscribers/${SUBSCRIBER_ID}`,
    wizardAdditionalAccountsFromSubscribers: '/wizard/additional-accounts/subscribers',
    wizardCollationReportFromAccounts: '/wizard/collation-report/accounts',
    wizardCollationReportFromAccountId: `/wizard/collation-report/account/${CLIENT_ID}`,
    wizardAdditionalReportsFromSubscribers: '/wizard/additional-reports/subscribers',
    wizardAdditionalReportsFromSubscriberId: `/wizard/additional-reports/subscribers/${CLIENT_ID}`,
    wizardAdditionalReportsFromAccounts: '/wizard/additional-reports/accounts',
    wizardAdditionalReportsFromAccountId: `/wizard/additional-reports/accounts/${CLIENT_ID}`,
    wizardBillsDocs: '/wizard/bills-docs/selection',
    wizardBillsDocsAccount: `/wizard/bills-docs/${CLIENT_ID}`,
    wizardBillsDocsUnpaid: '/wizard/bills-docs/unpaid',
    wizardBillsDocsUnpaidAccount: `/wizard/bills-docs/unpaid/${CLIENT_ID}`,
    wizardChangeLockFromSubscriberId: `/wizard/change-lock/subscribers/${SUBSCRIBER_ID}`,
    wizardChangeLockFromSubscribers: '/wizard/change-lock/subscribers',
    wizardExpensesDetail: '/wizard/detail-client-expenses',
    wizardExpensesDetailAccountId: `/wizard/detail-client-expenses/account/${CLIENT_ID}`,
    wizardImportPersonalData: '/wizard/import-personal-data',
    wizardMultiSearch: '/wizard/multi-search',
    wizardOrderTemplateCreate: '/wizard/order-template/create',
    wizardOrderTemplateCreateFromDashbord: '/constructor/wizard/order-template/create',
    wizardOrderTemplateCreateFromAccount: `/constructor/account/info/${CLIENT_ID}/wizard/order-template/create`,
    wizardOrderTemplateCreateFromAccountList: '/constructor/accounts/wizard/order-template/create',
    wizardOrderTemplateCreateFromMobileList: '/subscribers/mobile/constructor/wizard/order-template/create',
    wizardOrderTemplateCreateFromAutoReports: '/constructor/settings/autoreports/wizard/order-template/create',
    wizardOrderTemplateEdit: '/wizard/order-template/edit',
    wizardOrderTemplateEditFromDashbord: '/constructor/wizard/order-template/edit',
    wizardOrderTemplateEditFromAccount: `/constructor/account/info/${CLIENT_ID}/wizard/order-template/edit`,
    wizardOrderTemplateEditFromAccountList: '/constructor/accounts/wizard/order-template/edit',
    wizardOrderTemplateEditFromMobileList: '/subscribers/mobile/constructor/wizard/order-template/edit',
    wizardOrderTemplateEditFromAutoReports: '/constructor/settings/autoreports/wizard/order-template/edit',
    wizardOrderReport: '/wizard/order-report',
    wizardOrderReportFromDashbord: '/constructor/wizard/order-report',
    wizardOrderReportFromAccount: `/constructor/account/info/${CLIENT_ID}/wizard/order-report`,
    wizardOrderReportFromAccountList: '/constructor/accounts/wizard/order-report',
    wizardOrderReportFromMobileList: '/subscribers/mobile/constructor/wizard/order-report',
    wizardOrderReportFromAutoReports: '/constructor/settings/autoreports/wizard/order-report',
    wizardProductOfferings: '/wizard/product-offerings',
    wizardProductOfferingsFromCampaigns: `/wizard/product-offerings-campaign/${CLIENT_ID}`,
    wizardProductOfferingsFromSubscriberId: `/wizard/product-offerings/subscribers/${CLIENT_ID}`,
    wizardProductOfferingsFromSubscriberIdOnlyMegaPowers: `/wizard/product-offerings/subscribers/${CLIENT_ID}/only-mega-powers`,
    wizardProductOfferingsFromSubscribers: '/wizard/product-offerings/subscribers',
    wizardImportSubscribersLimits: '/wizard/limits-import',
    wizardChangeRoleFromSubscriberId: `/wizard/change-role/subscribers/${SUBSCRIBER_ID}`,
    wizardChangeRoleFromSubscribers: '/wizard/change-role/subscribers',
};

/*
 * Для старых роутов необходимо редиректить на новый (в зависимости от рычага)
 */
export const RoutePathOld: Partial<IRoutePath> = {
    subscriberIdInfo: `/subscriber/info/${CLIENT_ID}`,
};

export interface IRoutePath {
    account: string;
    accountInfo: string;
    accountInfoAccs: string;
    accountInfoExternal: string;
    accountInfoFinancial: string;
    accountInfoFinancialDiscounts: string;
    accountInfoFinancialGeneral: string;
    accountInfoFinancialMultiBalances: string;
    accountInfoGeneral: string;
    autoReportId: string;
    autoReportIdAccounts: string;
    autoReportIdAccountsEdit: string;
    autoReportIdFormatDelivery: string;
    autoReportIdScheduleParameters: string;
    autoReportIdSubscribers: string;
    autoReportIdSubscribersEdit: string;
    bills: string;
    billsAccount: string;
    billsUnpaid: string;
    billsUnpaidAccount: string;
    callsDetailDailyExpensesAccount: string;
    callsDetailDailyExpensesAccountId: string;
    callsDetailDailyExpensesAccountSingle: string;
    callsDetailDailyExpensesAccountSingleSubscribers: string;
    callsDetailDailyExpensesAccountSubscribers: string;
    callsDetailDailyExpensesBillsAccount: string;
    callsDetailDailyExpensesBillsAccountId: string;
    callsDetailDailyExpensesBillsAccountSingle: string;
    callsDetailDailyExpensesBillsAccountSingleSubscribers: string;
    callsDetailDailyExpensesBillsAccountsSubscribers: string;
    callsDetailDailyExpensesBillsUnpaidAccount: string;
    callsDetailDailyExpensesBillsUnpaidAccountId: string;
    callsDetailDailyExpensesBillsUnpaidAccountSingle: string;
    callsDetailDailyExpensesBillsUnpaidAccountSingleSubscribers: string;
    callsDetailDailyExpensesBillsUnpaidAccountsSubscribers: string;
    callsDetailDailyExpensesSubscriberMobile: string;
    callsDetailExpensesAccountId: string;
    callsDetailExpensesAccountIdSubscribers: string;
    callsDetailExpensesAccounts: string;
    callsDetailExpensesAccountsSubscribers: string;
    callsDetailExpensesBillsAccountId: string;
    callsDetailExpensesBillsAccountIdSubscribers: string;
    callsDetailExpensesBillsAccounts: string;
    callsDetailExpensesBillsAccountsSubscribers: string;
    callsDetailExpensesBillsUnpaidAccountId: string;
    callsDetailExpensesBillsUnpaidAccountIdSubscribers: string;
    callsDetailExpensesBillsUnpaidAccounts: string;
    callsDetailExpensesBillsUnpaidAccountsSubscribers: string;
    callsDetailExpensesSubscriberFixed: string;
    callsDetailExpensesSubscriberMobile: string;
    callsDetailFixed: string;
    callsDetailFixedInfo: string;
    callsDetailMobile: string;
    callsDetailMobileInfo: string;
    campaignInfo: string;
    campaigns: string;
    campaignsAccount: string;
    campaignsByClient: string;
    campaignsByMsisdn: string;
    campaignsSubscriber: string;
    cashback: string;
    cashbackAccountId: string;
    cashbackControlAccountId: string;
    cashbackHistoryAccountId: string;
    changePassword: string;
    collectiveProduct: string;
    dashboard: string;
    expensesAnalyzer: string;
    expensesAnalyzerAccount: string;
    expensesAnalyzerAccountId: string;
    expensesAnalyzerAccountIdSubscribers: string;
    expensesAnalyzerAccountSubscribers: string;
    expensesAnalyzerBillsAccountId: string;
    expensesAnalyzerBillsAccountIdSubscribers: string;
    expensesAnalyzerBillsAccounts: string;
    expensesAnalyzerBillsAccountsSubscribers: string;
    expensesAnalyzerBillsUnpaidAccountId: string;
    expensesAnalyzerBillsUnpaidAccountIdSubscribers: string;
    expensesAnalyzerBillsUnpaidAccounts: string;
    expensesAnalyzerBillsUnpaidAccountsSubscribers: string;
    expensesAnalyzerSubscriberFixed: string;
    expensesAnalyzerSubscriberFixedId: string;
    expensesAnalyzerSubscriberMobile: string;
    expensesAnalyzerSubscriberMobileId: string;
    fastInquiryCreate: string;
    fastAdvanceInvoice: string;
    fastPromisedPayment: string;
    wizardFastBillsDoc: string;
    wizardFastReportDoc: string;
    forbidden: string;
    inquiries: string;
    inquiryInfo: string;
    labelsImport: string;
    login: string;
    logout: string;
    main: string;
    moneyTransfer: string;
    moneyTransferAccountId: string;
    news: string;
    newsId: string;
    newsCreate: string;
    newsPreview: string;
    offers: string;
    orderId: string;
    orders: string;
    paymentsAccount: string;
    paymentsAccountId: string;
    products: string;
    productsFromSubscriberId: string;
    productsFromSubscribers: string;
    recovery: string;
    reports: string;
    reportsConstructor: string;
    reportsConstructorFromAccount: string;
    reportsConstructorFromAccountList: string;
    reportsConstructorFromMobileList: string;
    reportsConstructorFromAutoReports: string;
    reportsConstructorSubscriber: string;
    serviceAccessChangeUser: string;
    serviceAccessLogin: string;
    settings: string;
    settingsAutoReports: string;
    settingsLabels: string;
    settingsPositions: string;
    settingsProfile: string;
    settingsStatistics: string;
    settingsNotifications: string;
    subscriberFixed: string;
    subscriberFixedInfo: string;
    subscriberId: string;
    subscriberIdAdditionalAccounts: string;
    subscriberIdDcRule: string;
    subscriberIdFinances: string;
    subscriberIdInfo: string;
    subscriberIdManageNumber: string;
    subscriberIdPersonalData: string;
    subscriberIdProducts: string;
    subscriberMobile: string;
    unauthorized: string;

    // wizards
    wizardAdditionalAccountsFromSubscriberId: string;
    wizardAdditionalAccountsFromSubscribers: string;
    wizardCollationReportFromAccounts: string;
    wizardCollationReportFromAccountId: string;
    wizardAdditionalReportsFromSubscribers: string;
    wizardAdditionalReportsFromSubscriberId: string;
    wizardAdditionalReportsFromAccounts: string;
    wizardAdditionalReportsFromAccountId: string;
    wizardBillsDocs: string;
    wizardBillsDocsAccount: string;
    wizardBillsDocsUnpaid: string;
    wizardBillsDocsUnpaidAccount: string;
    wizardChangeLockFromSubscriberId: string;
    wizardChangeLockFromSubscribers: string;
    wizardExpensesDetail: string;
    wizardExpensesDetailAccountId: string;
    wizardImportPersonalData: string;
    wizardMultiSearch: string;
    wizardOrderTemplateCreate: string;
    wizardOrderTemplateCreateFromDashbord: string;
    wizardOrderTemplateCreateFromAccount: string;
    wizardOrderTemplateCreateFromAccountList: string;
    wizardOrderTemplateCreateFromMobileList: string;
    wizardOrderTemplateCreateFromAutoReports: string;
    wizardOrderTemplateEdit: string;
    wizardOrderTemplateEditFromDashbord: string;
    wizardOrderTemplateEditFromAccount: string;
    wizardOrderTemplateEditFromAccountList: string;
    wizardOrderTemplateEditFromMobileList: string;
    wizardOrderTemplateEditFromAutoReports: string;
    wizardOrderReport: string;
    wizardOrderReportFromDashbord: string;
    wizardOrderReportFromAccount: string;
    wizardOrderReportFromAccountList: string;
    wizardOrderReportFromMobileList: string;
    wizardOrderReportFromAutoReports: string;
    wizardProductOfferings: string;
    wizardProductOfferingsFromCampaigns: string;
    wizardProductOfferingsFromSubscriberId: string;
    wizardProductOfferingsFromSubscriberIdOnlyMegaPowers: string;
    wizardProductOfferingsFromSubscribers: string;
    wizardImportSubscribersLimits: string;
    wizardChangeRoleFromSubscriberId: string;
    wizardChangeRoleFromSubscribers: string;
}
